import React, { useMemo, useState } from "react";
import {
  getReasonLabel,
  LOG_ACTION_INCOMING_GOODS_REGISTERED,
  LOG_ACTION_QUANTITY_ADJUSTED,
  LOG_ACTION_RESTOCKED_UNPICK,
  LOG_ACTION_SOLD,
} from "../../../../constants/CratesConstants";
import { Button, DatePicker, Empty, Input, Popover, Select, Space, Table, Tag, Typography } from "antd";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import { useCratesStockLog } from "../../../../data/hooks/firestore/useCratesStockLog";
import { PRODUCT_COMPANY_GROUPS } from "../../../../constants/ProductConstant";
import { StyledSelect } from "../../../../styles/sharedStyles";
import useIsMobile from "../../../../hooks/useIsMobile";
import styled from "styled-components";

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const CratesLogTab = ({ tableY }) => {
  const { t } = useTranslation();
  const { products } = useProducts();
  const [customDateRange, setCustomDateRange] = useState(null);
  const [searchText, setSearchText] = useState("");
  const dateRange = useMemo(
    () => customDateRange || [dayjs().subtract(7, "days").startOf("day").unix(), dayjs().endOf("day").unix()],
    [customDateRange],
  );
  const { cratesLogs, isLoading } = useCratesStockLog(dateRange);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("All");
  const [orderIdSearch, setOrderIdSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const isMobileView = useIsMobile();

  const dataSource = useMemo(() => {
    const sortedLogs = [...cratesLogs].sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0));
    return sortedLogs.map((log) => {
      const events = Array.isArray(log.events) ? log.events : [];
      const eventsCount = events.length;

      const firstEvent = events[0] || null;
      const productId = firstEvent?.productId || log.productId || null;
      const productBarcode = firstEvent?.productBarcode || log.productBarcode || null;

      const product = products?.find(
        (p) => p?.id === productId || p?.productId === productId || p?.barcode === productBarcode,
      );
      const productName = product?.productName || product?.productNameNL || firstEvent?.productName || null;

      const uniqueProductIds = new Set(events.map((e) => e?.productId || e?.productBarcode).filter(Boolean));
      const hasMultipleProducts = uniqueProductIds.size > 1;

      return {
        key: log.id,
        ...log,
        eventsCount,
        productId,
        productBarcode,
        productName,
        hasMultipleProducts,
        events,
      };
    });
  }, [cratesLogs, products]);

  const eventColumns = useMemo(() => {
    return [
      {
        title: t("crate_stock.updated"),
        dataIndex: "createdAt",
        key: "createdAt",
        width: 160,
        render: (value) => {
          if (!value) return "-";
          return dayjs.unix(Number(value)).format("DD-MM-YYYY HH:mm");
        },
      },
      {
        title: t("type"),
        dataIndex: "type",
        key: "type",
        width: 220,
        ellipsis: true,
        render: (value) => {
          const typeColors = {
            [LOG_ACTION_INCOMING_GOODS_REGISTERED]: "blue",
            [LOG_ACTION_SOLD]: "green",
            [LOG_ACTION_RESTOCKED_UNPICK]: "orange",
            [LOG_ACTION_QUANTITY_ADJUSTED]: "purple",
            DISCARD_EXPIRED: "red",
          };
          return <Tag color={typeColors[value] || "default"}>{value || "-"}</Tag>;
        },
      },
      {
        title: t("product_name"),
        dataIndex: "productName",
        key: "productName",
        width: 200,
        ellipsis: true,
        render: (value) => value || "-",
      },
      {
        title: t("crate_stock.qty_before"),
        dataIndex: "quantityBefore",
        key: "quantityBefore",
        width: 120,
        render: (value, record) => {
          if (record.type === "DISCARD_EXPIRED" && Array.isArray(record.itemsDiscarded) && record.itemsDiscarded[0]) {
            const item = record.itemsDiscarded[0];
            return item.quantityBefore === 0 ? 0 : item.quantityBefore || "-";
          }
          return value === 0 ? 0 : value || "-";
        },
      },
      {
        title: t("crate_stock.qty_delta"),
        dataIndex: "delta",
        key: "delta",
        width: 90,
        render: (value, record) => {
          let deltaValue = value;
          if (record.type === "DISCARD_EXPIRED" && Array.isArray(record.itemsDiscarded) && record.itemsDiscarded[0]) {
            deltaValue = record.itemsDiscarded[0].delta;
          }
          if (deltaValue === 0) return "0";
          if (!deltaValue && deltaValue !== 0) return "-";
          const color = deltaValue > 0 ? "green" : "red";
          const prefix = deltaValue > 0 ? "+" : "";
          return (
            <Text style={{ color }}>
              {prefix}
              {deltaValue}
            </Text>
          );
        },
      },
      {
        title: t("crate_stock.qty"),
        dataIndex: "quantity",
        key: "quantity",
        width: 90,
        render: (value, record) => {
          if (record.type === "DISCARD_EXPIRED" && Array.isArray(record.itemsDiscarded) && record.itemsDiscarded[0]) {
            const item = record.itemsDiscarded[0];
            return item.quantity === 0 ? 0 : item.quantity || "-";
          }
          return value === 0 ? 0 : value || "-";
        },
      },
      {
        title: t("crate_stock.reason_label"),
        dataIndex: "reason",
        key: "reason",
        width: 200,
        ellipsis: true,
        render: (value, record) => {
          if (!value) return "-";
          const label = getReasonLabel(value, t);
          return record?.reasonNote ? `${label}: ${record.reasonNote}` : label;
        },
      },
      {
        title: t("order_id"),
        dataIndex: "farmorderId",
        key: "farmorderId",
        width: 140,
        ellipsis: true,
        render: (_, record) => {
          if (record?.type !== LOG_ACTION_SOLD && record?.type !== LOG_ACTION_RESTOCKED_UNPICK) return "-";
          return record?.farmorderId || record?.orderId || "-";
        },
      },
      {
        title: t("crate_stock.added_by"),
        dataIndex: "createdByName",
        key: "createdByName",
        width: 200,
        ellipsis: true,
        render: (_, record) =>
          record?.createdByName || record?.createdBy || record?.userEmail || record?.createdByUid || "-",
      },
    ];
  }, [t]);

  const columns = useMemo(() => {
    return [
      {
        title: t("crate_stock.crate_id"),
        dataIndex: "crateId",
        key: "crateId",
        width: 160,
        sorter: (a, b) => (a.crateId || "").localeCompare(b.crateId || ""),
        render: (value, record) => {
          const isExpanded = expandedRowKeys.includes(record.key);
          return (
            <Text
              className="text-button"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                setExpandedRowKeys((prevKeys) => {
                  if (prevKeys.includes(record.key)) {
                    return prevKeys.filter((key) => key !== record.key);
                  } else {
                    return [...prevKeys, record.key];
                  }
                });
              }}
            >
              {value || "-"}
            </Text>
          );
        },
      },
      {
        title: t("crate_stock.product"),
        dataIndex: "productName",
        key: "productName",
        width: 220,
        ellipsis: true,
        render: (value, record) => {
          if (record.hasMultipleProducts) {
            return "Mixed";
          }
          if (value) {
            return `${value} (${record.productId || record.productBarcode || ""})`;
          }
          return record.productId || record.productBarcode || "-";
        },
      },
      {
        title: t("crate_stock.company"),
        dataIndex: "company",
        key: "company",
        width: 70,
        render: (value) => value || "-",
      },
      {
        title: t("crate_stock.events"),
        dataIndex: "eventsCount",
        key: "eventsCount",
        width: 100,
        sorter: (a, b) => (a.eventsCount || 0) - (b.eventsCount || 0),
        render: (value) => value || 0,
      },
      {
        title: t("crate_stock.log_created"),
        dataIndex: "createdAt",
        key: "createdAt",
        width: 160,
        sorter: (a, b) => Number(a?.createdAt || 0) - Number(b?.createdAt || 0),
        sortOrder: "descend",
        render: (value) => {
          if (!value) return "-";
          return dayjs.unix(Number(value)).format("DD-MM-YYYY HH:mm");
        },
      },
    ];
  }, [t]);

  const expandedRowRender = (record) => {
    const events = record.events || [];

    if (events.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("crate_stock.no_events")}
          style={{ margin: "20px 0" }}
        />
      );
    }

    const sortedEvents = [...events].sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0));

    return (
      <Table
        showSorterTooltip={false}
        columns={eventColumns}
        dataSource={sortedEvents.map((event, index) => ({
          key: event.id || `event-${index}`,
          ...event,
        }))}
        pagination={false}
        size="small"
        style={{ marginLeft: 40, marginRight: 40 }}
      />
    );
  };

  const filteredDataSource = useMemo(() => {
    let result = dataSource;
    if (companyFilter && companyFilter !== "All") {
      result = result.filter((row) => row.company === companyFilter);
    }
    if (searchText) {
      const lower = searchText.toLowerCase();
      result = result.filter((row) => row.crateId && row.crateId.toLowerCase().includes(lower));
    }
    if (orderIdSearch) {
      const lower = orderIdSearch.toLowerCase();
      result = result.filter(
        (row) =>
          Array.isArray(row.events) &&
          row.events.some(
            (event) =>
              (event?.farmorderId && event.farmorderId.toLowerCase().includes(lower)) ||
              (event?.orderId && event.orderId.toLowerCase().includes(lower)),
          ),
      );
    }
    if (productSearch) {
      const lower = productSearch.toLowerCase();
      result = result.filter((row) => {
        const productNameMatch = row.productName && row.productName.toLowerCase().includes(lower);
        const productIdMatch = row.productId && row.productId.toLowerCase().includes(lower);
        const productBarcodeMatch = row.productBarcode && row.productBarcode.toLowerCase().includes(lower);
        return productNameMatch || productIdMatch || productBarcodeMatch;
      });
    }
    return result;
  }, [dataSource, searchText, companyFilter, orderIdSearch, productSearch]);

  // Default: last 7 days. Clearing the picker falls back to that.
  const handleDateRangeChange = (range) => {
    setCustomDateRange(range?.[0] && range?.[1] ? [range[0].startOf("day").unix(), range[1].endOf("day").unix()] : null);
  };

  return (
    <div className="table-responsive">
      <RangePicker
        style={{ marginBottom: 8 }}
        value={[dayjs.unix(dateRange[0]), dayjs.unix(dateRange[1])]}
        onChange={handleDateRangeChange}
        placeholder={[t("start_date"), t("end_date")]}
      />
      {!isMobileView ? (
        <Space style={{ marginBottom: 8, padding: 2 }}>
          <StyledSelect
            style={{ minWidth: 140 }}
            $isFiltered={companyFilter !== "All"}
            value={companyFilter}
            onChange={setCompanyFilter}
            placeholder={t("company")}
          >
            <Option value="All">{t("all_companies")}</Option>
            {PRODUCT_COMPANY_GROUPS.map((group) => (
              <Option key={group.value} value={group.value}>
                {group.label}
              </Option>
            ))}
          </StyledSelect>
          <Input
            style={{ maxWidth: 180 }}
            value={searchText}
            placeholder={t("crate_stock.crate_barcode_label")}
            prefix={<SearchOutlined />}
            allowClear
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Input
            style={{ maxWidth: 180 }}
            value={orderIdSearch}
            placeholder={t("order_id")}
            prefix={<SearchOutlined />}
            allowClear
            onChange={(e) => setOrderIdSearch(e.target.value)}
          />
          <Input
            style={{ maxWidth: 200 }}
            value={productSearch}
            placeholder={t("crate_stock.search_product")}
            prefix={<SearchOutlined />}
            allowClear
            onChange={(e) => setProductSearch(e.target.value)}
          />
        </Space>
      ) : (
        <div style={{ marginBottom: 8 }}>
          <Popover
            content={
              <FilterPopoverContent>
                <div>
                  <FilterLabel>{t("company")}</FilterLabel>
                  <Select
                    placeholder={t("company")}
                    style={{ width: "100%" }}
                    value={companyFilter}
                    onChange={setCompanyFilter}
                    size="large"
                  >
                    <Option value="All">{t("all_companies")}</Option>
                    {PRODUCT_COMPANY_GROUPS.map((group) => (
                      <Option key={group.value} value={group.value}>
                        {group.label}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <FilterLabel>{t("crate_stock.crate_barcode_label")}</FilterLabel>
                  <Input
                    value={searchText}
                    placeholder={t("crate_stock.crate_barcode_label")}
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={(e) => setSearchText(e.target.value)}
                    size="large"
                  />
                </div>
                <div>
                  <FilterLabel>{t("order_id")}</FilterLabel>
                  <Input
                    value={orderIdSearch}
                    placeholder={t("order_id")}
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={(e) => setOrderIdSearch(e.target.value)}
                    size="large"
                  />
                </div>
                <div>
                  <FilterLabel>{t("crate_stock.search_product")}</FilterLabel>
                  <Input
                    value={productSearch}
                    placeholder={t("crate_stock.search_product")}
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={(e) => setProductSearch(e.target.value)}
                    size="large"
                  />
                </div>
                <FilterButtonsContainer>
                  <Button
                    onClick={() => {
                      setSearchText("");
                      setCompanyFilter("All");
                      setOrderIdSearch("");
                      setProductSearch("");
                      setFilterPopoverVisible(false);
                    }}
                    style={{ flex: 1 }}
                  >
                    {t("reset")}
                  </Button>
                  <Button type="primary" onClick={() => setFilterPopoverVisible(false)} style={{ flex: 1 }}>
                    {t("apply")}
                  </Button>
                </FilterButtonsContainer>
              </FilterPopoverContent>
            }
            title={t("filters")}
            trigger="click"
            open={filterPopoverVisible}
            onOpenChange={setFilterPopoverVisible}
            placement="bottomLeft"
          >
            <Button
              icon={<FilterOutlined />}
              style={{
                borderColor: companyFilter !== "All" || searchText || orderIdSearch || productSearch ? "#FF4B33" : "",
                color: companyFilter !== "All" || searchText || orderIdSearch || productSearch ? "#FF4B33" : "",
              }}
            />
          </Popover>
        </div>
      )}
      <Table
        showSorterTooltip={false}
        columns={columns}
        dataSource={filteredDataSource}
        loading={isLoading}
        rowKey="key"
        sticky
        scroll={{ x: "max-content", y: tableY }}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showTotal: (total) => `${t("total")} ${total} ${t("items")}`,
        }}
        expandable={{
          expandedRowRender,
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
          showExpandColumn: false,
        }}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("crate_stock.no_results_date_range")} />,
        }}
      />
    </div>
  );
};

export default CratesLogTab;

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
`;
